import head from 'lodash/head';
import React from 'react';
import {
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  MultiMediaDelete,
  None,
} from 'dtos';
import {
  useNotify,
  useConfirmDialog,
  useMedia,
} from 'core/hooks';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  postApiMultimediaDeleteMultiMedia,
} from 'endpoints';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';

export type RequestMultiMediaDelete = {
  list?: (MultiMediaDelete & { fileName?: string | null })[] | null;
};

type ConfirmationMessageProps = {
  fileName: string;
};

function ConfirmationMessage({ fileName }: ConfirmationMessageProps) {
  return (
    <p>
      Are you sure you want to delete
      {' '}
      <strong>{fileName}</strong>
      ?
    </p>
  );
}

export default function useDeleteMediaHandler(
  originatorId?: number,
  originatorType: MediaOriginatorType = MediaOriginatorType.Patient,
) {
  const { invalidateMedia } = useMedia();
  const queryClient = useQueryClient();
  const { mutateAsync: deleteMedia, isPending } = useMutation<
    None,
    Error,
    RequestMultiMediaDelete>({
      mutationFn: (metaInfo) => postApiMultimediaDeleteMultiMedia(metaInfo),
    });

  const notify = useNotify();
  const { confirm, closeConfirm } = useConfirmDialog();

  const handleDelete = async (metaInfo: RequestMultiMediaDelete) => {
    await deleteMedia(metaInfo);
    mediaEventChannel.emit('onMediaDeleted', metaInfo);
    await queryClient.invalidateQueries({ queryKey: ['patient-multimedia-search'] });

    notify({
      message: 'Media was deleted successfully.',
      variant: 'success',
    });
    if ((originatorId ?? 0) > 0) {
      invalidateMedia(originatorId ?? 0, originatorType);
    }
    closeConfirm();
  };

  const onMediaDelete = (metaInfo: RequestMultiMediaDelete) => {
    const mediaList = metaInfo?.list ?? [];
    const fileName = head(mediaList)?.fileName ?? '';
    confirm({
      title: 'Delete Multimedia',
      content: (<ConfirmationMessage fileName={fileName} />),
      onConfirm: async () => {
        await handleDelete(metaInfo);
      },
    });
  };

  return { onMediaDelete, isPending };
}
